import { useQuery } from '@tanstack/react-query'
import type { FeatureFlagAssignment } from '@/app/commons/generated'
import { useOptionalUser } from '@/pages/user/selectors'
import httpService from './httpService'
import { Response } from '@/types'

/**
 * The server's own feature flags — the v2 targeting instance.
 *
 * Distinct from `useHasFeature`, which reads the Braze-backed remote config in Redux. Both are
 * "feature flags" and they answer different questions: Braze flags are marketing-owned and
 * evaluated in the SDK, while these are evaluated server-side against the authenticated user by the
 * same targeting engine that drives promotions and enforcement — so a rule can key off anything the
 * server knows (email domain, account age, club standing) without that data leaving DUPR.
 *
 * Reach for these when the audience is defined by DUPR data, and for Braze when marketing wants to
 * own the switch.
 */

export type { FeatureFlagAssignment }

/**
 * Every flag targeting this user, keyed by flag id.
 *
 * **A flag whose criteria did not match is ABSENT from this map, not present as `control`.** The
 * server drops non-matching rules before it builds the response, so "missing" is the ordinary
 * answer for most users and every reader has to treat it as the off state rather than as a failure
 * to load.
 */
export type FeatureFlagMap = Record<string, FeatureFlagAssignment>

/**
 * The typed payload on an assignment, narrowed.
 *
 * The generated `FeatureFlagAssignmentValue` is `{ type: string }` and nothing more: the server's
 * `FeatureFlagValue` is a sealed hierarchy whose subtypes each add a differently-typed `value`, and
 * that does not survive the round trip through the OpenAPI document. The discriminator is
 * generated; the payload is not.
 *
 * A narrowing of what the server actually sends, not a redefinition — `variant` and the envelope
 * still come from the generated models, which is where drift would otherwise start. Callers that
 * only gate on the variant key never touch this.
 */
export type FeatureFlagPayload =
  | { type: 'none' }
  | { type: 'string'; value: string }
  | { type: 'number'; value: number }
  | { type: 'boolean'; value: boolean }
  | { type: 'object'; value: unknown }

/** Read an assignment's payload in its narrowed form. */
export const payloadOf = (assignment: FeatureFlagAssignment | undefined): FeatureFlagPayload =>
  (assignment?.value as FeatureFlagPayload | undefined) ?? { type: 'none' }

export const FeatureFlagQueryKeys = {
  /** The prefix every scoped key extends — what to match when removing all of them at once. */
  all: ['feature-flags'] as const,
  /**
   * One key per user, and one per user rather than one per flag.
   *
   * Per user because assignment is per user: an unscoped key is a single cache entry that every
   * account signing in on this browser takes turns reading, so signing out of an account that has
   * a feature and back in as one that does not leaves the second reading the first one's answer
   * until something evicts it. Sign-out does clear the cache today, but that makes correctness
   * depend on every sign-out path remembering to; with the id in the key the entries are simply
   * different and the wrong one is unreadable.
   *
   * One per user rather than one per flag because the server answers every flag in a single
   * request — a key per flag would be several cache entries fed by one response, and several
   * requests to fill them.
   */
  forUser: (userId: string | number) => ['feature-flags', String(userId)] as const,
}

/** The `Result` envelope, whose `results` is a list holding the one map. */
type ResultsOf<T> = Response & { results: T[] }

/**
 * The server's flag assignments for the signed-in user.
 *
 * `staleTime` matches the `Cache-Control: private, max-age=60` the endpoint sets on itself, so the
 * client honors the contract the server states rather than inventing a second one. Respecting it
 * is cheap: the route does no IO — rules and their metadata are already in the engine's memory — so
 * a refetch costs a hash and a map lookup.
 *
 * A shorter window would not make a kill switch land any faster either. Rules are reloaded from
 * Postgres on a five-minute cadence (`TargetingRuleManager.REFRESH_INTERVAL_MS`), and that, not
 * this, is the floor on how quickly a flag change reaches anybody.
 *
 * Held until there is a session: assignment is per-user and the route is authenticated, so asking
 * anonymously buys a 401 and a retry. A signed-out reader gets `undefined`, which every consumer
 * already reads as "nothing is on".
 */
export const useFeatureFlags = () => {
  const user = useOptionalUser()
  return useQuery({
    // The placeholder is never fetched under — `enabled` holds the request until there is a user —
    // and exists only so the key is well formed while signed out.
    queryKey: FeatureFlagQueryKeys.forUser(user?.id ?? 'anonymous'),
    enabled: !!user,
    staleTime: 60 * 1000,
    queryFn: async ({ signal }) => {
      const res = await httpService.Get<ResultsOf<FeatureFlagMap>>('/api/feature-flags', { signal })
      // 200 with `status: FAILURE` is how the server reports that its own resolution failed. The
      // honest reading of "we could not work out your flags" is that no flag is on — throwing would
      // put every gated surface into an error state instead of the off state it already renders.
      if (res.status === 'FAILURE') return {}
      return res.results[0] ?? {}
    },
  })
}

/**
 * One flag's assignment, or `undefined` when it does not apply to this user.
 *
 * Undefined is the ordinary answer rather than an error: the server omits every rule whose criteria
 * the user failed, so most users are missing most flags. It is also what a caller gets while the
 * request is in flight and while signed out — deliberately indistinguishable, because a gate has to
 * render something in all three cases and "off" is the only safe choice for a feature that is not
 * yet generally available.
 */
export const useFeatureFlag = (flagKey: string): FeatureFlagAssignment | undefined => useFeatureFlags().data?.[flagKey]

/** Whether this user was bucketed into a specific variant of a flag. */
export const useFeatureVariant = (flagKey: string, variant: string): boolean =>
  useFeatureFlag(flagKey)?.variant === variant

/**
 * Whether the flag answer is settled — as opposed to still being fetched.
 *
 * Most callers do not want this. A badge or a menu row should render nothing until it knows, and
 * `useFeatureFlag` returning `undefined` already says so. It matters only where the user has
 * *asked* for a gated surface — a pasted or bookmarked URL — because there "not yet known" has to
 * hold rather than redirect them straight back out.
 *
 * Signed out counts as settled: there is no session, so there is nothing left to wait for. An
 * errored request counts as settled too — the answer is "no flags", which is the safe reading.
 */
export const useFeatureFlagsResolved = (): boolean => {
  const user = useOptionalUser()
  const { isLoading } = useFeatureFlags()
  // A disabled query never leaves its initial state, so the session check has to come first.
  if (!user) return true
  return !isLoading
}
